import { supabase } from '../lib/supabase';
import { errorHandler } from '../utils/errorHandler';
import { validationUtils } from '../utils/validation';
import { dataCache, performanceUtils } from '../utils/performance';

export const instituteService = {
  async getInstitutes(filters = {}) {
    return errorHandler?.withRetry(async () => {
      // Check cache first
      const cacheKey = `institutes_${JSON.stringify(filters)}`;
      const cached = dataCache?.get(cacheKey);
      if (cached) return cached;

      let query = supabase?.from('institute_profiles')?.select(`
        *,
        user:user_id(id, name, email, city, state),
        courses(id, course_name, category, duration, fees),
        virtual_booths(id, visit_count)
      `)?.eq('approval_status', 'approved');
    
      if (filters?.category && filters?.category !== 'all') {
        query = query?.contains('categories', [filters?.category]);
      }
      
      if (filters?.location && filters?.location !== 'all') {
        query = query?.eq('user.city', filters?.location);
      }
      
      const { data, error } = await performanceUtils?.timeout(10000, query);
      
      if (error) throw error;
      
      const transformed = data?.map(institute => ({
        id: institute?.id,
        userId: institute?.user_id,
        instituteName: institute?.institute_name,
        logoUrl: institute?.logo_url,
        bannerUrl: institute?.banner_url,
        description: institute?.description,
        website: institute?.website,
        contactPerson: institute?.contact_person,
        approvalStatus: institute?.approval_status,
        boothType: institute?.booth_type,
        categories: institute?.categories || [],
        user: institute?.user,
        courses: institute?.courses,
        virtualBooths: institute?.virtual_booths
      })) || [];
      
      // Cache the result
      dataCache?.set(cacheKey, transformed);
      
      return transformed;
    }, { maxRetries: 2, retryDelay: 1000 });
  },

  async getInstituteById(instituteId) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(instituteId)) {
        throw new Error('Invalid institute ID');
      }
      
      // Check cache
      const cacheKey = `institute_${instituteId}`;
      const cached = dataCache?.get(cacheKey);
      if (cached) return cached;
      
      const { data, error } = await supabase?.from('institute_profiles')?.select(`
        *,
        user:user_id(id, name, email, city, state),
        courses(*),
        virtual_booths(*)
      `)?.eq('id', instituteId)?.single();
      
      if (error) throw error;
      
      // Cache result
      if (data) {
        dataCache?.set(cacheKey, data);
      }
      
      return data;
    }, 'getInstituteById');
  },

  async getInstituteForBooth(instituteId) {
    return errorHandler?.withRetry(async () => {
      console.log('[getInstituteForBooth] Starting fetch for institute ID:', instituteId);
      
      // Silently return null for placeholder/invalid values
      if (!instituteId || instituteId === ':id' || instituteId === 'undefined' || instituteId === 'null') {
        console.warn('[getInstituteForBooth] Placeholder or missing institute ID, skipping fetch:', instituteId);
        return null;
      }

      if (!validationUtils?.isValidUUID(instituteId)) {
        console.warn('[getInstituteForBooth] Invalid UUID format, skipping fetch:', instituteId);
        return null;
      }
      
      // Check cache
      const cacheKey = `institute_booth_${instituteId}`;
      const cached = dataCache?.get(cacheKey);
      if (cached) {
        console.log('[getInstituteForBooth] Returning cached data for:', instituteId);
        return cached;
      }
      
      console.log('[getInstituteForBooth] Executing Supabase query...');
      const { data, error } = await performanceUtils?.timeout(10000, 
        supabase
          ?.from('institute_profiles')
          ?.select(`
            *,
            user:user_id(id, name, email, phone, city, state),
            courses(id, course_name, category, duration, fees, eligibility, course_type, specialization, level, show_fee),
            virtual_booths(id, video_urls, brochure_urls, application_links, visit_count, campus_tour_urls)
          `)
          ?.eq('id', instituteId)
          ?.single()
      );
      
      if (error) {
        console.error('[getInstituteForBooth] Supabase query error:', {
          error,
          instituteId,
          errorCode: error?.code,
          errorMessage: error?.message,
          errorDetails: error?.details,
          errorHint: error?.hint
        });
        
        // Provide more specific error messages
        if (error?.code === 'PGRST116') {
          throw new Error(`Institute not found with ID: ${instituteId}. Please verify the booth ID is correct.`);
        }
        
        throw error;
      }
      
      console.log('[getInstituteForBooth] Query successful. Data received:', {
        instituteId: data?.id,
        instituteName: data?.institute_name,
        hasUser: !!data?.user,
        coursesCount: data?.courses?.length || 0,
        hasVirtualBooths: !!data?.virtual_booths,
        virtualBoothsData: data?.virtual_booths
      });
      
      // Log warning if institute exists but has no virtual_booths record
      if (data && (!data?.virtual_booths || data?.virtual_booths?.length === 0)) {
        console.warn('[getInstituteForBooth] Institute found but has no virtual_booths record:', {
          instituteId,
          instituteName: data?.institute_name
        });
      }
      
      // Cache result
      if (data) {
        dataCache?.set(cacheKey, data);
        console.log('[getInstituteForBooth] Data cached successfully');
      }
      
      return data;
    }, { maxRetries: 2, retryDelay: 1000 });
  },

  async getSavedInstitutes(studentId) {
    const { data, error } = await supabase?.from('saved_institutes')?.select(`
        *,
        institute:institute_id(
          *,
          user:user_id(id, name, email, city, state),
          courses(id, course_name, category)
        )
      `)?.eq('student_id', studentId)?.order('saved_at', { ascending: false });
    
    if (error) throw error;
    return data?.map(item => item?.institute);
  },

  async saveInstitute(studentId, instituteId) {
    const { data, error } = await supabase?.from('saved_institutes')?.insert({
        student_id: studentId,
        institute_id: instituteId
      })?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  async unsaveInstitute(studentId, instituteId) {
    const { error } = await supabase?.from('saved_institutes')?.delete()?.eq('student_id', studentId)?.eq('institute_id', instituteId);
    
    if (error) throw error;
  },

  async createInstitute(formData) {
    const {
      instituteName,
      email,
      password,
      phone,
      contactPerson,
      city,
      state,
      website,
      description,
      boothType,
      categories,
    } = formData || {};

    if (!email || !password || !instituteName) {
      throw new Error('Institute Name, Email, and Password are required');
    }

    const { data: fnData, error: fnError } = await supabase?.functions?.invoke('create-institute-user', {
      body: {
        email: email?.trim()?.toLowerCase(),
        password,
        instituteName: instituteName?.trim(),
        city: city?.trim() || null,
        state: state?.trim() || null,
        phone: phone?.trim() || null,
        website: website?.trim() || null,
        categories: Array.isArray(categories) ? categories : [],
      },
    });

    if (fnError) {
      let msg = fnError?.message || String(fnError);
      try {
        const contextBody = fnError?.context;
        if (contextBody) {
          let parsed = contextBody;
          if (typeof contextBody === 'string') {
            parsed = JSON.parse(contextBody);
          } else if (contextBody instanceof Response) {
            const text = await contextBody?.text();
            parsed = JSON.parse(text);
          } else if (contextBody?.json) {
            parsed = await contextBody?.json();
          }
          if (parsed?.error) msg = parsed?.error;
          else if (parsed?.message) msg = parsed?.message;
        }
      } catch (_) {}

      if (msg?.includes('already exists') || msg?.includes('already registered') || msg?.includes('duplicate')) {
        throw new Error('This email address is already in use. Please use a different email.');
      }
      throw new Error(msg);
    }

    if (!fnData?.success) {
      let msg = fnData?.error || 'Failed to create institute account';
      if (msg?.includes('already exists') || msg?.includes('already registered') || msg?.includes('duplicate')) {
        throw new Error('This email address is already in use. Please use a different email.');
      }
      throw new Error(msg);
    }

    // Update booth type and contact person if provided (edge function sets defaults)
    if (fnData?.userId && (boothType || contactPerson || description)) {
      try {
        const updatePayload = {};
        if (boothType) updatePayload.booth_type = boothType;
        if (contactPerson) updatePayload.contact_person = contactPerson;
        if (description) updatePayload.description = description;

        await supabase
          ?.from('institute_profiles')
          ?.update(updatePayload)
          ?.eq('user_id', fnData?.userId);
      } catch (_) {
        // Non-fatal: profile was created, extra fields can be updated later
      }
    }

    return { 
      success: true, 
      userId: fnData?.userId, 
      email: fnData?.email, 
      instituteName: fnData?.instituteName,
      recovered: fnData?.recovered || false
    };
  },

  async updateInstituteApprovalStatus(instituteId, status) {
    const { data, error } = await supabase?.from('institute_profiles')?.update({
      approval_status: status
    })?.eq('id', instituteId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  async getAllInstitutesForAdmin(filters = {}) {
    let query = supabase?.from('institute_profiles')?.select(`
        *,
        user:user_id(id, name, email, city, state, created_at, plain_password),
        courses(id, course_name, category)
      `);

    if (filters?.approvalStatus && filters?.approvalStatus !== 'all') {
      query = query?.eq('approval_status', filters?.approvalStatus);
    }

    // Explicitly specify institute_profiles.created_at to avoid ambiguity with user.created_at
    const { data, error } = await query?.order('created_at', { ascending: false, foreignTable: null });

    if (error) throw error;

    return data?.map(institute => ({
      id: institute?.id,
      userId: institute?.user_id,
      instituteName: institute?.institute_name,
      logoUrl: institute?.logo_url,
      bannerUrl: institute?.banner_url,
      description: institute?.description,
      website: institute?.website,
      contactPerson: institute?.contact_person,
      approvalStatus: institute?.approval_status,
      boothType: institute?.booth_type,
      aiAssistantName: institute?.ai_assistant_name,
      chatEnabled: institute?.chat_enabled ?? false,
      videoEnabled: institute?.video_enabled ?? false,
      tour360Enabled: institute?.tour_360_enabled ?? false,
      categories: institute?.categories || [],
      isFeatured: institute?.is_featured ?? false,
      ranking: institute?.ranking ?? null,
      state: institute?.state ?? null,
      feesMin: institute?.fees_min ?? null,
      feesMax: institute?.fees_max ?? null,
      rankingsData: institute?.rankings_data ?? [],
      accreditations: institute?.accreditations ?? [],
      awards: institute?.awards ?? null,
      user: institute?.user,
      courses: institute?.courses
    }));
  },

  async updateInstituteActivation(instituteId, { chatEnabled, videoEnabled, tour360Enabled }) {
    const updatePayload = {};
    if (chatEnabled !== undefined) updatePayload.chat_enabled = chatEnabled;
    if (videoEnabled !== undefined) updatePayload.video_enabled = videoEnabled;
    if (tour360Enabled !== undefined) updatePayload.tour_360_enabled = tour360Enabled;
    updatePayload.updated_at = new Date()?.toISOString();

    const { data, error } = await supabase
      ?.from('institute_profiles')
      ?.update(updatePayload)
      ?.eq('id', instituteId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  async updateInstituteProfile(instituteId, updateData) {
    const updatePayload = {};
    
    if (updateData?.instituteName) updatePayload.institute_name = updateData?.instituteName;
    if (updateData?.description !== undefined) updatePayload.description = updateData?.description;
    if (updateData?.website !== undefined) updatePayload.website = updateData?.website;
    if (updateData?.contactPerson !== undefined) updatePayload.contact_person = updateData?.contactPerson;
    if (updateData?.boothType) updatePayload.booth_type = updateData?.boothType;
    if (updateData?.aiAssistantName !== undefined) updatePayload.ai_assistant_name = updateData?.aiAssistantName;
    
    updatePayload.updated_at = new Date()?.toISOString();

    const { data, error } = await supabase?.from('institute_profiles')?.update(updatePayload)?.eq('id', instituteId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  async getInstituteProfile(userId) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(userId)) {
        throw new Error('Invalid user ID');
      }
      
      const { data, error } = await supabase?.from('institute_profiles')?.select('*')?.eq('user_id', userId)?.maybeSingle();
      
      // Handle PGRST116 error (no rows found) gracefully
      if (error && error?.code !== 'PGRST116') {
        throw error;
      }
      
      if (!data) {
        throw new Error('Institute profile not found');
      }
      
      return {
        id: data?.id,
        userId: data?.user_id,
        instituteName: data?.institute_name,
        logoUrl: data?.logo_url,
        bannerUrl: data?.banner_url,
        description: data?.description,
        website: data?.website,
        contactPerson: data?.contact_person,
        approvalStatus: data?.approval_status,
        boothType: data?.booth_type,
        aiAssistantName: data?.ai_assistant_name,
        categories: data?.categories || []
      };
    }, 'getInstituteProfile');
  },

  async deleteInstitute(instituteId) {
    // First get the user_id associated with this institute
    const { data: institute, error: fetchError } = await supabase?.from('institute_profiles')?.select('user_id')?.eq('id', instituteId)?.single();

    if (fetchError) throw fetchError;

    const userId = institute?.user_id;

    // Delete all related records in correct dependency order before deleting user
    // 1. Delete chat messages (depend on chat_conversations)
    const { data: conversations } = await supabase?.from('chat_conversations')?.select('id')?.eq('institute_id', userId);
    if (conversations?.length > 0) {
      const conversationIds = conversations?.map(c => c?.id);
      await supabase?.from('ai_chat_analytics')?.delete()?.in('conversation_id', conversationIds);
      await supabase?.from('chat_messages')?.delete()?.in('conversation_id', conversationIds);
    }

    // 2. Delete chat conversations
    await supabase?.from('chat_conversations')?.delete()?.eq('institute_id', userId);

    // 3. Delete group session registrations (depend on group_counseling_sessions)
    const { data: groupSessions } = await supabase?.from('group_counseling_sessions')?.select('id')?.eq('institute_id', userId);
    if (groupSessions?.length > 0) {
      const sessionIds = groupSessions?.map(s => s?.id);
      await supabase?.from('group_session_registrations')?.delete()?.in('session_id', sessionIds);
    }
    await supabase?.from('group_counseling_sessions')?.delete()?.eq('institute_id', userId);

    // 4. Delete counselor bookings (depend on institute_availability)
    await supabase?.from('counselor_bookings')?.delete()?.eq('institute_id', userId);

    // 5. Delete institute_availability
    await supabase?.from('institute_availability')?.delete()?.eq('institute_id', userId);

    // 6. Delete other institute-related records
    await supabase?.from('counseling_sessions')?.delete()?.eq('institute_id', userId);
    await supabase?.from('leads')?.delete()?.eq('institute_id', userId);
    await supabase?.from('booth_gallery')?.delete()?.eq('institute_id', userId);
    await supabase?.from('booth_reviews')?.delete()?.eq('institute_id', userId);
    await supabase?.from('booth_resources')?.delete()?.eq('institute_id', userId);
    await supabase?.from('booth_visits')?.delete()?.eq('institute_id', userId);
    await supabase?.from('testimonials')?.delete()?.eq('institute_id', userId);
    await supabase?.from('ai_chat_analytics')?.delete()?.eq('institute_id', userId);
    await supabase?.from('faqs')?.delete()?.eq('institute_id', userId);
    await supabase?.from('notifications')?.delete()?.eq('user_id', userId);
    await supabase?.from('analytics_events')?.delete()?.eq('user_id', userId);

    // 7. Delete institute profile (courses, virtual_booths, saved_institutes, events cascade from institute_profiles.id)
    const { error: profileError } = await supabase?.from('institute_profiles')?.delete()?.eq('id', instituteId);
    if (profileError) throw profileError;

    // 8. Delete user account (all remaining FK references should be gone)
    const { error: userError } = await supabase?.from('users')?.delete()?.eq('id', userId);
    if (userError) throw userError;

    return { success: true };
  },

  async getFeaturedInstitutes() {
    return errorHandler?.withRetry(async () => {
      const { data, error } = await supabase
        ?.from('institute_profiles')
        ?.select(`
          *,
          user:user_id(id, name, email, city, state),
          courses(id, course_name, category, duration, fees),
          virtual_booths(id, visit_count)
        `)
        ?.eq('approval_status', 'approved')
        ?.eq('is_featured', true)
        ?.order('ranking', { ascending: true, nullsFirst: false })
        ?.limit(8);

      if (error) throw error;

      return data?.map(institute => ({
        id: institute?.id,
        instituteName: institute?.institute_name,
        logoUrl: institute?.logo_url,
        bannerUrl: institute?.banner_url,
        description: institute?.description,
        boothType: institute?.booth_type,
        categories: institute?.categories || [],
        ranking: institute?.ranking,
        state: institute?.state || institute?.user?.state,
        feesMin: institute?.fees_min,
        feesMax: institute?.fees_max,
        user: institute?.user,
        courses: institute?.courses,
        virtualBooths: institute?.virtual_booths
      })) || [];
    }, { maxRetries: 2, retryDelay: 1000 });
  },

  async getInstitutesWithFilters({ search = '', state = '', category = '', feesMin = null, feesMax = null, ranking = '', page = 1, pageSize = 24 } = {}) {
    return errorHandler?.withRetry(async () => {
      let query = supabase
        ?.from('institute_profiles')
        ?.select(`
          *,
          user:user_id(id, name, email, city, state),
          courses(id, course_name, category, duration, fees),
          virtual_booths(id, visit_count)
        `, { count: 'exact' })
        ?.eq('approval_status', 'approved');

      if (search) {
        query = query?.ilike('institute_name', `%${search}%`);
      }

      if (state && state !== 'all') {
        query = query?.or(`state.eq.${state},user.state.eq.${state}`);
      }

      if (category && category !== 'all') {
        query = query?.contains('categories', [category]);
      }

      if (feesMin !== null && feesMin !== '') {
        query = query?.gte('fees_min', Number(feesMin));
      }

      if (feesMax !== null && feesMax !== '') {
        query = query?.lte('fees_max', Number(feesMax));
      }

      if (ranking === 'ranked') {
        query = query?.not('ranking', 'is', null)?.order('ranking', { ascending: true });
      } else {
        query = query?.order('institute_name', { ascending: true });
      }

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query?.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      const transformed = data?.map(institute => ({
        id: institute?.id,
        instituteName: institute?.institute_name,
        logoUrl: institute?.logo_url,
        bannerUrl: institute?.banner_url,
        description: institute?.description,
        boothType: institute?.booth_type,
        categories: institute?.categories || [],
        ranking: institute?.ranking,
        state: institute?.state || institute?.user?.state,
        feesMin: institute?.fees_min,
        feesMax: institute?.fees_max,
        user: institute?.user,
        courses: institute?.courses,
        virtualBooths: institute?.virtual_booths
      })) || [];

      return { data: transformed, total: count || 0 };
    }, { maxRetries: 2, retryDelay: 1000 });
  },

  async setFeaturedInstitute(instituteId, isFeatured) {
    const { error } = await supabase
      ?.from('institute_profiles')
      ?.update({ is_featured: isFeatured })
      ?.eq('id', instituteId);

    if (error) throw error;
    return { success: true };
  },

  async getExhibitionHallInstitutes({ search = '', state = '', category = '', feesMin = null, feesMax = null, ranking = '', page = 1, pageSize = 24 } = {}) {
    return errorHandler?.withRetry(async () => {
      let query = supabase
        ?.from('institute_profiles')
        ?.select(`
          *,
          user:user_id(id, name, email, city, state),
          courses(id, course_name, category, duration, fees),
          virtual_booths(id, visit_count)
        `, { count: 'exact' })
        ?.eq('approval_status', 'approved')
        ?.eq('in_exhibition_hall', true);

      if (search) {
        query = query?.ilike('institute_name', `%${search}%`);
      }

      if (state && state !== 'all') {
        query = query?.or(`state.eq.${state},user.state.eq.${state}`);
      }

      if (category && category !== 'all') {
        query = query?.contains('categories', [category]);
      }

      if (feesMin !== null && feesMin !== '') {
        query = query?.gte('fees_min', Number(feesMin));
      }

      if (feesMax !== null && feesMax !== '') {
        query = query?.lte('fees_max', Number(feesMax));
      }

      if (ranking === 'ranked') {
        query = query?.not('ranking', 'is', null)?.order('ranking', { ascending: true });
      } else {
        query = query?.order('institute_name', { ascending: true });
      }

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query?.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      const transformed = data?.map(institute => ({
        id: institute?.id,
        instituteName: institute?.institute_name,
        logoUrl: institute?.logo_url,
        bannerUrl: institute?.banner_url,
        description: institute?.description,
        boothType: institute?.booth_type,
        categories: institute?.categories || [],
        ranking: institute?.ranking,
        state: institute?.state || institute?.user?.state,
        feesMin: institute?.fees_min,
        feesMax: institute?.fees_max,
        user: institute?.user,
        courses: institute?.courses,
        virtualBooths: institute?.virtual_booths
      })) || [];

      return { data: transformed, total: count || 0 };
    }, { maxRetries: 2, retryDelay: 1000 });
  },

  async updateInstituteRankingAndState(instituteId, { ranking, state, feesMin, feesMax, rankingsData, accreditations, awards }) {
    const updateData = {};
    if (ranking !== undefined) updateData.ranking = ranking ? Number(ranking) : null;
    if (state !== undefined) updateData.state = state || null;
    if (feesMin !== undefined) updateData.fees_min = feesMin ? Number(feesMin) : null;
    if (feesMax !== undefined) updateData.fees_max = feesMax ? Number(feesMax) : null;
    if (rankingsData !== undefined) updateData.rankings_data = rankingsData || [];
    if (accreditations !== undefined) updateData.accreditations = accreditations || [];
    if (awards !== undefined) updateData.awards = awards || null;

    const { error } = await supabase
      ?.from('institute_profiles')
      ?.update(updateData)
      ?.eq('id', instituteId);

    if (error) throw error;
    return { success: true };
  }
};